import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { Setting, NewSetting } from "@/types";
import { RootState } from "@/store/store";

export const contentSettingsApi = createApi({
  reducerPath: "contentSettings",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["contentSettings"],
  endpoints: (builder) => ({
    fetchContentSettings: builder.query<Setting[], { organizationId: string | SkipToken; contentId: string | SkipToken }>({
      queryFn: async ({ organizationId, contentId }) => {
        try {
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content/${String(contentId)}/settings`);

          const response = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Setting[]);
          });

          return { data: response as Setting[] };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["contentSettings"],
    }),
    addSetting: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          payload,
        }: {
          organizationId: string;
          contentId: string | SkipToken;
          payload: NewSetting;
        },
        { getState }
      ) => {
        try {
          const { auth } = getState() as RootState;

          if (!auth?.user?.uid) {
            return { data: undefined };
          }

          const draft: NewSetting = {
            ...payload,
            min: payload.min ?? 1,
            max: payload.max ?? 1,
            createdBy: auth.user.uid,
            createdAt: Date.now(),
          };

          const collectionRef = collection(db, `organizations/${organizationId}/content/${String(contentId)}/settings`);
          const response = await addDoc(collectionRef, draft);
          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["contentSettings"],
    }),
    updateContentSetting: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          payload,
        }: {
          organizationId: string | SkipToken;
          contentId: string;
          payload: Setting;
        },
        { getState }
      ) => {
        try {
          const { auth } = getState() as RootState;

          if (!organizationId || !contentId || !auth?.user?.uid || !payload?.id) {
            throw new Error("Invalid payload");
          }

          const draft: Setting = {
            ...payload,
            lastUpdatedBy: auth?.user?.uid,
            lastUpdatedAt: Date.now(),
          };

          const docRef = doc(db, `organizations/${String(organizationId)}/content/${contentId}/settings/${payload.id}`);
          const response = await updateDoc(docRef, draft);

          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["contentSettings"],
    }),
    deleteContentSetting: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          settingId,
        }: {
          organizationId: string | SkipToken;
          contentId: string;
          settingId: string;
        },
        { getState }
      ) => {
        try {
          const { auth } = getState() as RootState;

          if (!organizationId || !contentId || !settingId || !auth?.user?.uid) {
            throw new Error("Invalid payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/content/${contentId}/settings/${settingId}`);
          const response = await deleteDoc(docRef);

          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["contentSettings"],
    }),
  }),
});

export const { useFetchContentSettingsQuery, useLazyFetchContentSettingsQuery, useAddSettingMutation, useUpdateContentSettingMutation, useDeleteContentSettingMutation } =
  contentSettingsApi;
